import React from 'react';
import { Link } from 'react-router-dom';

// Helper to parse inline markdown (bold, links, italics)
export function renderInline(text, keyPrefix = '') {
  if (!text) return null;

  // Split by bold (**...**) and links ([...](...))
  const parts = [];
  let remaining = text;
  let counter = 0;

  // Regex to match bold **...** or link [...](...) or newline \\
  const inlineRegex = /(\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)|\\\\)/g;
  let match;
  let lastIndex = 0;

  while ((match = inlineRegex.exec(remaining)) !== null) {
    if (match.index > lastIndex) {
      parts.push(remaining.substring(lastIndex, match.index));
    }

    if (match[0] === '\\\\') {
      parts.push(<br key={`${keyPrefix}-br-${counter++}`} />);
    } else if (match[1].startsWith('**')) {
      // Bold
      parts.push(
        <strong key={`${keyPrefix}-bold-${counter++}`} className="font-bold text-slate-900">
          {match[2]}
        </strong>
      );
    } else if (match[1].startsWith('[')) {
      // Link: [text](href)
      const linkText = match[3];
      const href = match[4];

      if (href.startsWith('http://') || href.startsWith('https://')) {
        // If it points to billundvvs.netlify.app internal link, convert to relative router link
        const localMatch = href.match(/^https?:\/\/billundvvs\.netlify\.app(\/.*)?$/);
        if (localMatch) {
          const path = localMatch[1] ? localMatch[1].replace(/\/+$/, '') || '/' : '/';
          parts.push(
            <Link
              key={`${keyPrefix}-link-${counter++}`}
              to={path}
              className="text-[#d3102e] hover:text-[#a50d26] underline font-medium"
            >
              {linkText}
            </Link>
          );
        } else {
          parts.push(
            <a
              key={`${keyPrefix}-extlink-${counter++}`}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#d3102e] hover:text-[#a50d26] underline font-medium"
            >
              {linkText}
            </a>
          );
        }
      } else if (href.startsWith('tel:') || href.startsWith('mailto:')) {
        parts.push(
          <a
            key={`${keyPrefix}-tel-${counter++}`}
            href={href}
            className="text-[#d3102e] hover:text-[#a50d26] font-semibold underline"
          >
            {linkText}
          </a>
        );
      } else {
        // Internal relative link
        parts.push(
          <Link
            key={`${keyPrefix}-intlink-${counter++}`}
            to={href}
            className="text-[#d3102e] hover:text-[#a50d26] underline font-medium"
          >
            {linkText}
          </Link>
        );
      }
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < remaining.length) {
    parts.push(remaining.substring(lastIndex));
  }

  return parts;
}

// Parses full markdown string into structured elements
export function parseMarkdownBlocks(markdownText) {
  if (!markdownText) return { title: '', lead: '', blocks: [] };

  const lines = markdownText.split('\n');
  let title = '';
  let lead = '';
  let inHero = true;
  let remainingLines = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    // Skip skip-to-content links
    if (line.includes('Spring til indhold') || line.includes('Skip to content')) {
      continue;
    }

    if (line.startsWith('# ') && !title) {
      title = line.replace(/^#\s+/, '');
      continue;
    }

    // Lead paragraph right under H1
    if (title && inHero && line && !line.startsWith('[Få et uforpligtende tilbud') && !line.startsWith('[Get a free quote') && !line.startsWith('## ')) {
      if (!lead) {
        lead = line;
      }
      continue;
    }

    // Skip CTA buttons right after hero
    if (line.includes('Få et uforpligtende tilbud') || line.includes('Get a free quote') || line.includes('Ring 75 33 12 76') || line.includes('Call +45 75 33 12 76')) {
      inHero = false;
      continue;
    }

    inHero = false;
    remainingLines.push(lines[i]);
  }

  // Parse remaining lines into blocks
  const blocks = [];
  let currentTable = null;
  let currentList = null;

  const flushTable = () => {
    if (currentTable) {
      blocks.push({ type: 'table', headers: currentTable.headers, rows: currentTable.rows });
      currentTable = null;
    }
  };

  const flushList = () => {
    if (currentList) {
      blocks.push({ type: currentList.type, items: currentList.items });
      currentList = null;
    }
  };

  for (let i = 0; i < remainingLines.length; i++) {
    const line = remainingLines[i].trim();

    if (!line) {
      flushTable();
      flushList();
      continue;
    }

    // Table rows
    if (line.startsWith('|') && line.endsWith('|')) {
      flushList();
      const cells = line.split('|').map((c) => c.trim()).filter((c, idx, arr) => idx > 0 && idx < arr.length - 1);
      if (!currentTable) {
        currentTable = { headers: cells, rows: [] };
      } else if (line.includes('---')) {
        // separator row, skip
        continue;
      } else {
        currentTable.rows.push(cells);
      }
      continue;
    } else {
      flushTable();
    }

    // Image: ![alt](src)
    const imgMatch = line.match(/^!\[(.*?)\]\((.*?)\)$/);
    if (imgMatch) {
      flushList();
      blocks.push({ type: 'image', alt: imgMatch[1], src: imgMatch[2] });
      continue;
    }

    // Heading 2
    if (line.startsWith('## ')) {
      flushList();
      blocks.push({ type: 'h2', text: line.replace(/^##\s+/, '') });
      continue;
    }

    // Heading 3
    if (line.startsWith('### ')) {
      flushList();
      blocks.push({ type: 'h3', text: line.replace(/^###\s+/, '') });
      continue;
    }

    // Unordered List (- or *)
    if (line.startsWith('- ') || line.startsWith('* ')) {
      if (!currentList || currentList.type !== 'ul') {
        flushList();
        currentList = { type: 'ul', items: [] };
      }
      currentList.items.push(line.replace(/^[-*]\s+/, ''));
      continue;
    }

    // Ordered List (1. ...)
    const numMatch = line.match(/^(\d+)\.\s+(.*)$/);
    if (numMatch) {
      if (!currentList || currentList.type !== 'ol') {
        flushList();
        currentList = { type: 'ol', items: [] };
      }
      currentList.items.push({ num: numMatch[1], text: numMatch[2] });
      continue;
    }

    // Standalone Question in FAQs (e.g. "Hvad koster...", "Kan jeg opsige...")
    if (line.endsWith('?') && !line.startsWith('#')) {
      flushList();
      blocks.push({ type: 'faq_q', text: line });
      continue;
    }

    // Normal paragraph
    flushList();
    blocks.push({ type: 'p', text: line });
  }

  flushTable();
  flushList();

  return { title, lead, blocks };
}
